import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { ErrorHelper } from '@shared/helpers/error.helper';
import { MessageHelper, MessageTypeEnum } from '@shared/helpers/message.helper';
import { AuthService } from '@shared/services/auth.service';

@Component({
  selector: 'app-security',
  templateUrl: './security.component.html',
  styleUrls: ['./security.component.scss'],
})
export class SecurityComponent implements OnInit {
  form: FormGroup;

  @Output() onPasswordChanged = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private messageHelper: MessageHelper,
    private errorHelper: ErrorHelper,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      currentPassword: [null, Validators.required],
      newPassword: [
        null,
        [Validators.required, this.validateTriggerOther('confirmPassword')],
      ],
      confirmPassword: [
        null,
        [Validators.required, this.validateEqualsTo('newPassword')],
      ],
    });
  }

  async onSubmit() {
    this.showAllErrors();

    if (this.form.controls.confirmPassword.hasError('equalsTo')) {
      return this.messageHelper.send(
        'New Password and Password confirmation are different',
        MessageTypeEnum.error
      );
    }

    if (this.form.invalid) {
      return this.messageHelper.send(
        'All fields are required',
        MessageTypeEnum.error
      );
    }

    try {
      const { currentPassword, newPassword } = this.form.value;
      await this.authService.changePassword(currentPassword, newPassword);
      this.messageHelper.send('Password changed', MessageTypeEnum.success);
      this.form.reset();
      this.onPasswordChanged.emit();
    } catch (err) {
      this.errorHelper.catch(err);
    } finally {
    }
  }

  showFieldError(field: string, error: string = null) {
    const formField = this.form.get(field);

    const showError =
      formField.invalid && (formField.dirty || formField.touched);

    if (showError && error) {
      return formField.hasError(error);
    }

    return showError;
  }

  private showAllErrors() {
    for (let field in this.form.controls) {
      const control = this.form.get(field);

      control.markAsTouched();
      control.markAsDirty();
    }
  }

  private validateTriggerOther(field: string) {
    return (control: AbstractControl) => {
      if (control && control.parent) {
        control.parent.get(field).updateValueAndValidity();
      }

      return null;
    };
  }

  private validateEqualsTo(field: string) {
    return (control: AbstractControl) => {
      if (control && control.parent) {
        const otherControl = control.parent.get(field);

        if (
          control.value &&
          otherControl.value &&
          control.value !== otherControl.value
        ) {
          return {
            equalsTo: {
              field,
              invalid: true,
            },
          };
        }
      }

      return null;
    };
  }
}
